import React, { useState } from 'react';
import { Layout, Card, Typography, List, Space, Tag, Button, Empty, Row, Col, Select, Tabs, Input, Form, Radio } from 'antd';
import { DragOutlined, BarChartOutlined, FilterOutlined, DownloadOutlined } from '@ant-design/icons';
import { Column, Line } from '@ant-design/plots';
import { useParams, useNavigate } from 'react-router-dom';

const { Content, Sider } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;

// Mock Data for Dataset Columns
const MOCK_COLUMNS = [
  { id: 'col1', name: 'Order Date', type: 'date', category: 'dimension' },
  { id: 'col2', name: 'Region', type: 'string', category: 'dimension' },
  { id: 'col3', name: 'Product Category', type: 'string', category: 'dimension' },
  { id: 'col4', name: 'Revenue', type: 'number', category: 'measure' },
  { id: 'col5', name: 'Cost', type: 'number', category: 'measure' },
  { id: 'col6', name: 'Profit', type: 'number', category: 'measure' },
  { id: 'col7', name: 'Customer Segment', type: 'string', category: 'dimension' },
];

interface DraggableColumnProps {
  column: typeof MOCK_COLUMNS[0];
  onDragStart: (e: React.DragEvent, column: typeof MOCK_COLUMNS[0]) => void;
}

const DraggableColumn: React.FC<DraggableColumnProps> = ({ column, onDragStart }) => (
  <div
    draggable
    onDragStart={(e) => onDragStart(e, column)}
    style={{
      padding: '8px 12px',
      marginBottom: '6px',
      backgroundColor: '#fff',
      border: '1px solid #e8e8e8',
      borderRadius: '6px',
      cursor: 'move',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      transition: 'all 0.2s',
      boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
    }}
    onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = '#1890ff';
        e.currentTarget.style.backgroundColor = '#f0faff';
    }}
    onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = '#e8e8e8';
        e.currentTarget.style.backgroundColor = '#fff';
    }}
  >
    <Space>
       <DragOutlined style={{ color: '#bfbfbf', fontSize: '12px' }} />
       <Text style={{ fontSize: '13px' }}>{column.name}</Text>
    </Space>
    <span style={{ 
        fontSize: '10px', 
        color: column.category === 'measure' ? '#1890ff' : '#52c41a',
        background: column.category === 'measure' ? '#e6f7ff' : '#f6ffed',
        padding: '2px 6px',
        borderRadius: '4px',
        border: `1px solid ${column.category === 'measure' ? '#91d5ff' : '#b7eb8f'}`
    }}>
      {column.type === 'string' ? 'T' : '#'}
    </span>
  </div>
);

interface DropZoneProps {
  title: string;
  accepts: string[];
  items: typeof MOCK_COLUMNS;
  onDrop: (e: React.DragEvent) => void;
  onRemove: (id: string) => void;
  style?: React.CSSProperties;
}

const DropZone: React.FC<DropZoneProps> = ({ title, items, onDrop, onRemove, style }) => (
  <div style={{ marginBottom: '24px', ...style }}>
    <Text strong style={{ display: 'block', marginBottom: '8px', fontSize: '13px', color: '#595959' }}>{title}</Text>
    <div 
        onDragOver={(e) => {
            e.preventDefault();
            e.currentTarget.style.borderColor = '#1890ff';
            e.currentTarget.style.background = '#f0faff';
        }}
        onDragLeave={(e) => {
            e.currentTarget.style.borderColor = '#d9d9d9';
            e.currentTarget.style.background = '#fafafa';
        }}
        onDrop={(e) => {
            onDrop(e);
            e.currentTarget.style.borderColor = '#d9d9d9';
            e.currentTarget.style.background = '#fafafa';
        }}
        style={{
            background: '#fafafa',
            border: '1px dashed #d9d9d9',
            borderRadius: '8px',
            padding: '12px',
            minHeight: '100px',
            transition: 'all 0.2s',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px'
        }}
    >
    {items.length === 0 ? (
      <div style={{ padding: '20px 0', textAlign: 'center', color: '#bfbfbf', fontSize: '12px' }}>
        T
        <span style={{ display: 'block', marginTop: '4px' }}>Drop columns here</span>
      </div>
    ) : (
      items.map(item => (
        <div 
          key={item.id} 
          style={{ 
              padding: '6px 10px', 
              background: '#fff', 
              border: '1px solid #e8e8e8', 
              borderRadius: '4px',
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
          }}
        >
          <Space size={8}>
            <span style={{ 
                fontSize: '10px', 
                color: item.category === 'measure' ? '#1890ff' : '#52c41a',
                background: item.category === 'measure' ? '#e6f7ff' : '#f6ffed',
                padding: '1px 5px',
                borderRadius: '4px',
                fontWeight: 600
            }}>
                {item.type === 'string' ? 'T' : '#'}
            </span>
            <span style={{ fontSize: '13px', color: '#262626' }}>{item.name}</span>
          </Space>
          <Button 
            type="text" 
            size="small" 
            icon={<span style={{ fontSize: '14px', lineHeight: 1 }}>×</span>} 
            onClick={() => onRemove(item.id)}
            style={{ color: '#bfbfbf', minWidth: '20px', height: '20px', padding: 0 }}
            onMouseEnter={(e) => e.currentTarget.style.color = '#ff4d4f'}
            onMouseLeave={(e) => e.currentTarget.style.color = '#bfbfbf'}
          />
        </div>
      ))
    )}
    </div>
  </div>
);

const ReportBuilderPage: React.FC = () => {
    const { datasetId, workspaceId } = useParams();
    const navigate = useNavigate();

    // Config state
    const [config, setConfig] = useState<{
        xAxis: typeof MOCK_COLUMNS; // Renamed conceptually to 'Columns / Group By'
        yAxis: typeof MOCK_COLUMNS; // Renamed conceptually to 'Summarise By'
        filters: typeof MOCK_COLUMNS;
        sorting: typeof MOCK_COLUMNS;
        downloadColumns: typeof MOCK_COLUMNS;
    }>({
        xAxis: [],
        yAxis: [],
        filters: [],
        sorting: [],
        downloadColumns: []
    });

    const [chartType, setChartType] = useState('column');

    const handleDatasetChange = (value: string) => {
       navigate(`/${workspaceId || 'workspace'}/visualise/report/${value}`);
    };

    const handleDragStart = (e: React.DragEvent, column: typeof MOCK_COLUMNS[0]) => {
        e.dataTransfer.setData('column', JSON.stringify(column));
    };

    const handleDrop = (e: React.DragEvent, zone: keyof typeof config) => {
        e.preventDefault();
        const columnData = e.dataTransfer.getData('column');
        if (columnData) {
            const column = JSON.parse(columnData);
            setConfig(prev => ({
                ...prev,
                [zone]: [...prev[zone], column]
            }));
        }
    };

    const handleRemove = (zone: keyof typeof config, id: string) => {
        setConfig(prev => ({
            ...prev,
            [zone]: prev[zone].filter(item => item.id !== id)
        }));
    };

    // Mock Chart Data Logic
    const getChartData = () => {
        const data = [
            { x: 'Jan', y: 100 },
            { x: 'Feb', y: 120 },
            { x: 'Mar', y: 150 },
            { x: 'Apr', y: 190 },
            { x: 'May', y: 220 },
            { x: 'Jun', y: 280 },
        ];
        return data;
    };

    // Generic Chart Config
    const chartConfig = {
        data: getChartData(),
        xField: 'x',
        yField: 'y',
        height: 400,
        color: '#1890ff',
    };

    const tabsItems = [
        {
            key: 'data',
            label: 'Data',
            children: (
                <div style={{ height: 'calc(100vh - 160px)', overflowY: 'auto', paddingRight: '8px' }}>
                    <DropZone 
                        title="Filters" 
                        accepts={['dimension', 'measure']} 
                        items={config.filters}
                        onDrop={(e) => handleDrop(e, 'filters')}
                        onRemove={(id) => handleRemove('filters', id)}
                    />
                    <DropZone 
                        title="Summarise By" 
                        accepts={['measure']} 
                        items={config.yAxis}
                        onDrop={(e) => handleDrop(e, 'yAxis')}
                        onRemove={(id) => handleRemove('yAxis', id)}
                    />
                     <DropZone 
                        title="Column Sections" 
                        accepts={['dimension']} 
                        items={config.xAxis}
                        onDrop={(e) => handleDrop(e, 'xAxis')} 
                        onRemove={(id) => handleRemove('xAxis', id)}
                    />
                    <DropZone 
                        title="Sorting" 
                        accepts={['dimension', 'measure']} 
                        items={config.sorting}
                        onDrop={(e) => handleDrop(e, 'sorting')}
                        onRemove={(id) => handleRemove('sorting', id)}
                    />
                </div>
            )
        },
        {
            key: 'visualise',
            label: 'Visualise',
            children: (
                <div style={{ padding: '4px' }}>
                    <Title level={5}>Chart Type</Title>
                    <Select 
                        style={{ width: '100%', marginBottom: '16px' }}
                        value={chartType}
                        onChange={setChartType}
                    >
                        <Option value="column">Column Chart</Option>
                        <Option value="line">Line Chart</Option>
                        <Option value="pie">Pie Chart</Option>
                        <Option value="table">Table</Option>
                    </Select>

                    <Title level={5}>General Options</Title>
                    <Card size="small" style={{ marginBottom: '16px' }}>
                        <Form layout="vertical">
                            <Form.Item label="Orientation">
                                <Radio.Group defaultValue="vertical">
                                    <Radio.Button value="vertical">Vertical</Radio.Button>
                                    <Radio.Button value="horizontal">Horizontal</Radio.Button>
                                </Radio.Group>
                            </Form.Item>
                        </Form>
                    </Card>

                    <Title level={5}>Chart Config</Title>
                    <Card size="small" style={{ marginBottom: '16px' }}>
                        <Form layout="vertical">
                             <Form.Item label="Color Theme">
                                <Select defaultValue="default">
                                    <Option value="default">Default</Option>
                                    <Option value="dark">Dark</Option>
                                    <Option value="vibrant">Vibrant</Option>
                                </Select>
                            </Form.Item>
                        </Form>
                    </Card>

                     <Title level={5}>Conditional Formatting</Title>
                     <Card size="small">
                        <Empty description="No rules configured" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        <Button type="dashed" block icon={<FilterOutlined />}>Add Rule</Button>
                     </Card>
                </div>
            )
        },
        {
            key: 'download',
            label: 'Download',
            children: (
                 <div style={{ padding: '4px' }}>
                     <Title level={5}>Download Options</Title>
                     <DropZone 
                        title="Additional Columns to Export" 
                        accepts={['dimension', 'measure']} 
                        items={config.downloadColumns}
                        onDrop={(e) => handleDrop(e, 'downloadColumns')}
                        onRemove={(id) => handleRemove('downloadColumns', id)}
                    />
                    <Button type="primary" icon={<DownloadOutlined />} block style={{ marginTop: '16px' }}>
                        Download CSV
                    </Button>
                 </div>
            )
        }
    ];



    return (
        <Layout style={{ height: 'calc(100vh - 64px)', background: '#fff' }}>
            <Sider 
                width={300} 
                theme="light" 
                style={{ borderRight: '1px solid #f0f0f0', padding: '16px', overflowY: 'auto' }}
            >
                <div>
                    <div style={{ marginBottom: '24px' }}>
                        <Text strong style={{ display: 'block', marginBottom: '8px' }}>Select Dataset</Text>
                        <Select 
                            style={{ width: '100%' }}
                            placeholder="Select a Dataset"
                            value={datasetId}
                            onChange={handleDatasetChange}
                        >
                            <Option value="1">Sales Orders</Option>
                            <Option value="2">Customer Master</Option>
                            <Option value="3">Inventory Logs</Option>
                        </Select>
                    </div>

                    {!datasetId ? (
                        <Empty description="Select a dataset above to see columns" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    ) : (
                        <div style={{ padding: '0 4px' }}>
                            <div style={{ marginBottom: '20px' }}>
                                <Text type="secondary" style={{ fontSize: '12px', textTransform: 'uppercase', marginBottom: '8px', display: 'block' }}>Dimensions</Text>
                                {MOCK_COLUMNS.filter(c => c.category === 'dimension').map(col => (
                                    <DraggableColumn key={col.id} column={col} onDragStart={handleDragStart} />
                                ))}
                            </div>

                            <div>
                                <Text type="secondary" style={{ fontSize: '12px', textTransform: 'uppercase', marginBottom: '8px', display: 'block' }}>Measures</Text>
                                {MOCK_COLUMNS.filter(c => c.category === 'measure').map(col => (
                                    <DraggableColumn key={col.id} column={col} onDragStart={handleDragStart} />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </Sider>

            <Content style={{ padding: '24px', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>

                <Row gutter={24} style={{ height: '100%' }}>
                    {/* Configuration Panel (Middle) */}
                    <Col span={7} style={{ borderRight: '1px solid #f0f0f0', height: '100%', paddingRight: '16px' }}>
                       <Tabs defaultActiveKey="data" items={tabsItems} />
                    </Col>

                    {/* Visualization Panel (Right) */}
                    <Col span={17} style={{ paddingLeft: '16px' }}>
                         <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                            <Title level={4} style={{ margin: 0 }}>Visualization</Title>
                            <Space>
                                <Button icon={<BarChartOutlined />} type="primary" ghost>Visualize</Button>
                                <Button>Save Report</Button>
                            </Space>
                         </div>

                         <div style={{ background: '#fafafa', padding: '24px', borderRadius: '8px', minHeight: '500px' }}>
                            {config.xAxis.length > 0 && config.yAxis.length > 0 ? (
                                <Column {...chartConfig} />
                            ) : (
                                <Empty 
                                    image={Empty.PRESENTED_IMAGE_SIMPLE} 
                                    description="Drag columns to 'Column Sections' and 'Summarise By' to generate a chart" 
                                />
                            )}
                         </div>
                    </Col>
                </Row>
            </Content>
        </Layout>
    );
};

export default ReportBuilderPage;
